// FilePond cuts a long file name at the end, which hides the extension, the one part a provider
// needs to tell why a file was rejected. This plugin cuts it in the middle instead, and only for
// FilePond boxes that switch on allowFileNameMiddleEllipsis.

declare module 'filepond' {
    export interface FilePondOptions {
        /** Cut long file names in the middle, keeping the extension visible. */
        allowFileNameMiddleEllipsis?: boolean;
    }
}

const TAIL_CONTEXT = 8;

// A real extension is short and has no spaces. Text after a dot in a sentence, as in
// "Report Dr. Jane Smith", is not one, and keeping it whole would push the tail over the status.
const EXTENSION = /\.[^.\s]{1,5}$/;

export const splitFileNameForMiddleEllipsis = (name: string): { head: string, tail: string } => {
    const match = EXTENSION.exec(name);
    const extension = match && match.index > 0 ? match[0] : '';
    const tailLength = Math.min(name.length, extension.length + TAIL_CONTEXT);
    return {head: name.slice(0, name.length - tailLength), tail: name.slice(name.length - tailLength)};
};

const span = (className: string, text?: string) => {
    const element = document.createElement('span');
    element.className = className;
    if (text !== undefined) {
        element.textContent = text;
    }
    return element;
};

type FileInfoView = {
    element: HTMLElement;
    ref: { fileName: HTMLElement };
    is: (name: string) => boolean;
    query: (key: string) => unknown;
};

type PluginApi = {
    addFilter: (key: string, cb: (root: { view: FileInfoView }) => void) => void;
    utils: { Type: { BOOLEAN: unknown } };
};

export const FilePondPluginFileNameMiddleEllipsis = ({addFilter, utils}: PluginApi) => {
    addFilter('CREATE_VIEW', ({view}) => {
        if (!view.is('file-info') || !view.query('GET_ALLOW_FILE_NAME_MIDDLE_ELLIPSIS')) {
            return;
        }
        // FilePond rewrites the text of its own name span on later updates, so the cut lives in a
        // sibling and the original is only hidden. The name is already set when the view is created.
        const fileName = view.ref.fileName;
        const {head, tail} = splitFileNameForMiddleEllipsis(fileName.textContent ?? '');
        const middle = span('filepond--file-info-middle');
        middle.setAttribute('aria-hidden', 'true');
        middle.append(span('filepond--file-info-middle-head', head), span('filepond--file-info-middle-tail', tail));
        fileName.style.display = 'none';
        fileName.after(middle);
    });

    return {
        options: {
            allowFileNameMiddleEllipsis: [false, utils.Type.BOOLEAN],
        },
    };
};
